// UTM Health Snapshot scoring engine (PRD §19).
// Pure functions — no IO. Same module runs in the browser (paste flow)
// and on the Worker (GA4 aggregator flow at M4+).

import { toCsv } from "../csv";
import {
  evaluateCampaignStructure,
  evaluateGlobalFormat,
  evaluateVocabulary,
  loadRuleset,
  type Ruleset,
} from "./naming-conventions";
import { SOURCE_TO_PLATFORM, type UtmIssue, type UtmRow, type UtmScoreResult } from "./types";

// Category weights — sum = 100. Renormalised when the naming-convention
// ruleset added three new categories (`formatting`, `vocabulary`,
// `anti-pattern`); existing categories shaved 2–5 points each so the new
// ones can carry weight without exceeding 100. Total still 100, so the
// score remains a clean percentage rather than a clamp-floored quantity.
export const WEIGHTS = {
  "required-params": 35,
  "canonical-naming": 18,
  "campaign-structure": 12,
  "platform-metadata": 8,
  "content-granularity": 8,
  "dark-social-leakage": 5,
  // Added with the naming-convention ruleset.
  formatting: 5,
  vocabulary: 5,
  "anti-pattern": 4,
} as const;

interface RowEvaluation {
  issues: Set<string>; // category:label keys for unique counting per row
}

export interface ScoreRowsOptions {
  /** Active naming-convention ruleset. Defaults to `loadRuleset()` (sked).
   *  Pure injection point — keeps `scoreRows` testable and lets a per-tenant
   *  override land later without touching call sites that don't pick a
   *  preset. */
  ruleset?: Ruleset;
}

/** Score a batch of rows. Pure — returns a new UtmScoreResult. */
export function scoreRows(rows: UtmRow[], options: ScoreRowsOptions = {}): UtmScoreResult {
  const ruleset = options.ruleset ?? loadRuleset();
  if (rows.length === 0) {
    return {
      score: 0,
      band: "broken",
      issues: [],
      totals: { rows: 0, sessions: 0, flaggedRows: 0, flaggedSessions: 0 },
      flagged: [],
    };
  }

  const totalSessions = rows.reduce((acc, r) => acc + (r.sessions ?? 1), 0);

  // Per-category accumulators.
  const buckets: Record<
    UtmIssue["category"],
    Map<
      string,
      {
        severity: UtmIssue["severity"];
        label: string;
        detail: string;
        rows: Set<number>;
        sessions: number;
        examples: string[];
      }
    >
  > = {
    "required-params": new Map(),
    "canonical-naming": new Map(),
    "campaign-structure": new Map(),
    "platform-metadata": new Map(),
    "content-granularity": new Map(),
    "dark-social-leakage": new Map(),
    formatting: new Map(),
    vocabulary: new Map(),
    "anti-pattern": new Map(),
  };

  const rowEvaluations: RowEvaluation[] = rows.map(() => ({ issues: new Set() }));

  // Track distinct values to detect casing/synonym drift at the batch level.
  const sourceVariants = new Map<string, Set<string>>(); // lowercase → set of raw
  const mediumVariants = new Map<string, Set<string>>();

  rows.forEach((row, idx) => {
    const sessions = row.sessions ?? 1;
    const evaluation = rowEvaluations[idx];

    // 1. Required parameters present
    if (!row.source)
      addIssue(
        "required-params",
        "missing-source",
        "severe",
        "Missing utm_source",
        row,
        idx,
        sessions,
      );
    if (!row.medium)
      addIssue(
        "required-params",
        "missing-medium",
        "severe",
        "Missing utm_medium",
        row,
        idx,
        sessions,
      );
    if (!row.campaign)
      addIssue(
        "required-params",
        "missing-campaign",
        "severe",
        "Missing utm_campaign",
        row,
        idx,
        sessions,
      );

    // 2. Canonical naming consistency — track variants for cross-row detection
    if (row.source) {
      const lower = row.source.toLowerCase();
      if (!sourceVariants.has(lower)) sourceVariants.set(lower, new Set());
      sourceVariants.get(lower)!.add(row.source);

      // Synonym drift vs canonical — if the source has a canonical mapping
      // but differs from the canonical form, flag it
      const canonical = SOURCE_TO_PLATFORM[lower];
      if (canonical && row.source !== canonical.toLowerCase() && row.source !== canonical) {
        addIssue(
          "canonical-naming",
          `synonym-${lower}`,
          "medium",
          `'${row.source}' is a synonym of '${canonical}'`,
          row,
          idx,
          sessions,
        );
      }
    }

    if (row.medium) {
      const lower = row.medium.toLowerCase();
      if (!mediumVariants.has(lower)) mediumVariants.set(lower, new Set());
      mediumVariants.get(lower)!.add(row.medium);
      // Off-allowlist + too-generic medium checks now live in the
      // `vocabulary` evaluator (see naming-conventions.ts). The legacy
      // `canonical-naming:non-canonical-medium-*` hit retired so a single
      // signal fires per off-vocab medium rather than two.
    }

    // 3. Campaign structure — flag short slugs (< 4 chars). The new
    // `formatting` and `anti-pattern` evaluators (below) handle the
    // lowercase / hyphen / banned-vague checks the original scorer
    // explicitly punted on.
    if (row.campaign && row.campaign.length < 4) {
      addIssue(
        "campaign-structure",
        "campaign-too-short",
        "mild",
        `Campaign '${row.campaign}' is very short (<4 chars)`,
        row,
        idx,
        sessions,
      );
    }

    // 5. Platform metadata
    if (row.source && !row.sourcePlatform) {
      addIssue(
        "platform-metadata",
        "missing-source-platform",
        "mild",
        "Missing utm_source_platform (advisory)",
        row,
        idx,
        sessions,
      );
    }

    // 6. Content granularity
    if (!row.content && row.source) {
      addIssue(
        "content-granularity",
        "missing-content",
        "mild",
        "Missing utm_content — hard to attribute creative variations",
        row,
        idx,
        sessions,
      );
    }

    // 7. Dark-social leakage — row has a social-looking source but no manual campaign tagging
    if (row.source) {
      const lower = row.source.toLowerCase();
      const looksSocial = !!SOURCE_TO_PLATFORM[lower] || lower.includes("social");
      if (looksSocial && (!row.medium || !row.campaign)) {
        addIssue(
          "dark-social-leakage",
          "social-without-manual-tags",
          "medium",
          "Social-looking source with no utm_medium or utm_campaign",
          row,
          idx,
          sessions,
        );
      }
    }

    // 8. Naming-convention checks — formatting (lowercase / hyphens /
    // special chars), vocabulary (off-allowlist source / medium), and
    // anti-pattern (generic-name / YYYYMMDD) per the active ruleset.
    // Each evaluator returns RuleHit[] which we funnel through addIssue
    // so the existing bucketing + scoring pipeline carries them.
    const fieldsToFormat: Array<
      [string | undefined, "source" | "medium" | "campaign" | "content" | "term"]
    > = [
      [row.source, "source"],
      [row.medium, "medium"],
      [row.campaign, "campaign"],
      [row.content, "content"],
      [row.term, "term"],
    ];
    for (const [value, param] of fieldsToFormat) {
      for (const hit of evaluateGlobalFormat(value, param, ruleset)) {
        addIssue(hit.category, hit.key, hit.severity, hit.label, row, idx, sessions, hit.detail);
      }
    }
    if (row.source) {
      // Suppress vocabulary:off-allowlist-source when the synonym rule
      // already fires — the synonym rule names the canonical replacement
      // (`ig` → `Instagram`) which is more actionable than a generic
      // off-vocab message. Both rules pointing at the same row would
      // double-count without adding signal.
      const lower = row.source.toLowerCase();
      const synonymWillFire =
        SOURCE_TO_PLATFORM[lower] !== undefined &&
        row.source !== SOURCE_TO_PLATFORM[lower] &&
        row.source !== SOURCE_TO_PLATFORM[lower].toLowerCase();
      for (const hit of evaluateVocabulary(row.source, "source", ruleset)) {
        if (synonymWillFire && hit.key.startsWith("off-allowlist-source-")) continue;
        addIssue(hit.category, hit.key, hit.severity, hit.label, row, idx, sessions, hit.detail);
      }
    }
    if (row.medium) {
      for (const hit of evaluateVocabulary(row.medium, "medium", ruleset)) {
        addIssue(hit.category, hit.key, hit.severity, hit.label, row, idx, sessions, hit.detail);
      }
    }
    if (row.campaign) {
      for (const hit of evaluateCampaignStructure(row.campaign, ruleset)) {
        addIssue(hit.category, hit.key, hit.severity, hit.label, row, idx, sessions, hit.detail);
      }
    }

    // Cross-row: casing drift detection
    for (const [lower, variants] of sourceVariants) {
      if (variants.size > 1) {
        if (row.source && row.source.toLowerCase() === lower) {
          addIssue(
            "canonical-naming",
            `casing-drift-source-${lower}`,
            "medium",
            `Multiple casings for utm_source: ${Array.from(variants).sort().join(", ")}`,
            row,
            idx,
            sessions,
          );
        }
      }
    }
    for (const [lower, variants] of mediumVariants) {
      if (variants.size > 1) {
        if (row.medium && row.medium.toLowerCase() === lower) {
          addIssue(
            "canonical-naming",
            `casing-drift-medium-${lower}`,
            "medium",
            `Multiple casings for utm_medium: ${Array.from(variants).sort().join(", ")}`,
            row,
            idx,
            sessions,
          );
        }
      }
    }

    function addIssue(
      category: UtmIssue["category"],
      key: string,
      severity: UtmIssue["severity"],
      label: string,
      r: UtmRow,
      rowIdx: number,
      weight: number,
      /** Optional richer detail for the AI prompt + tooltips. Defaults
       *  to `label` to preserve the original behaviour for callers that
       *  pass a single string. */
      detail: string = label,
    ): void {
      const bucket = buckets[category];
      const entry = bucket.get(key) ?? {
        severity,
        label,
        detail,
        rows: new Set<number>(),
        sessions: 0,
        examples: [] as string[],
      };
      // Upgrade severity if a stronger variant shows up.
      if (severityRank(severity) > severityRank(entry.severity)) entry.severity = severity;
      entry.rows.add(rowIdx);
      entry.sessions += weight;
      if (entry.examples.length < 3) {
        const example = exampleForRow(r);
        if (example && !entry.examples.includes(example)) entry.examples.push(example);
      }
      bucket.set(key, entry);
      evaluation.issues.add(`${category}:${key}`);
    }
  });

  // Flatten buckets → UtmIssue[]
  const issues: UtmIssue[] = [];
  for (const [category, bucket] of Object.entries(buckets) as Array<
    [UtmIssue["category"], (typeof buckets)[UtmIssue["category"]]]
  >) {
    for (const entry of bucket.values()) {
      issues.push({
        category,
        severity: entry.severity,
        label: entry.label,
        detail: entry.detail,
        examples: entry.examples,
        rowCount: entry.rows.size,
        sessionCount: entry.sessions,
      });
    }
  }

  // Score computation: start at 100, deduct weighted penalties per category.
  // No per-category cap — when a row is missing all three required params the
  // penalty should legitimately saturate the score toward 0. Final clamp
  // guarantees the [0, 100] band. Severity factors: severe 100%, medium 60%,
  // mild 25%.
  let score = 100;
  for (const [category, weight] of Object.entries(WEIGHTS) as Array<
    [UtmIssue["category"], number]
  >) {
    const categoryIssues = issues.filter((i) => i.category === category);
    if (categoryIssues.length === 0) continue;
    let penalty = 0;
    for (const issue of categoryIssues) {
      const share = issue.sessionCount / totalSessions;
      const severityFactor =
        issue.severity === "severe" ? 1 : issue.severity === "medium" ? 0.6 : 0.25;
      penalty += weight * share * severityFactor;
    }
    score -= penalty;
  }
  score = Math.max(0, Math.min(100, Math.round(score)));

  // Flagged-rows list
  const flagged = rows
    .map((row, idx) => {
      const eval_ = rowEvaluations[idx];
      if (eval_.issues.size === 0) return null;
      return { ...row, issues: Array.from(eval_.issues) };
    })
    .filter((r): r is NonNullable<typeof r> => r !== null);

  const flaggedSessions = flagged.reduce((acc, r) => acc + (r.sessions ?? 1), 0);

  // Telemetry — one structured log line per scoreRows call so Cloudflare
  // Logpush captures rule-hit distribution. Lets us spot a runaway
  // false-positive rule before users complain.
  //
  // The hit keys here are stable rule names (`formatting:uppercase-source`,
  // `vocabulary:off-allowlist-medium-*`) — not the human-readable labels,
  // which embed raw user-controlled UTM values. Keeping the labels out
  // of the log keeps the payload free of values a user might consider
  // sensitive (e.g. internal partner names in a campaign tag).
  if (issues.length > 0) {
    const hits: Record<string, number> = {};
    // Reach back through the per-row evaluation to read the stable
    // `${category}:${key}` strings the scorer already tracks.
    for (const evaluation of rowEvaluations) {
      for (const tag of evaluation.issues) {
        // Trim the value-bearing suffix so e.g.
        // `vocabulary:off-allowlist-medium-customvalue` reduces to
        // `vocabulary:off-allowlist-medium-*`.
        const sanitised = tag.replace(/^(\w[\w-]*?):([\w-]*?-)?[^:]+$/, "$1:$2*");
        hits[sanitised] = (hits[sanitised] ?? 0) + 1;
      }
    }
    console.log(
      JSON.stringify({
        evt: "utm_audit_hits",
        ruleset: ruleset.id,
        version: ruleset.version,
        score,
        rows: rows.length,
        hits,
      }),
    );
  }

  return {
    score,
    band: score >= 90 ? "excellent" : score >= 75 ? "good" : score >= 50 ? "needs-work" : "broken",
    issues: issues.sort(
      (a, b) =>
        severityRank(b.severity) - severityRank(a.severity) || b.sessionCount - a.sessionCount,
    ),
    totals: {
      rows: rows.length,
      sessions: totalSessions,
      flaggedRows: flagged.length,
      flaggedSessions,
    },
    flagged,
  };
}

function severityRank(s: UtmIssue["severity"]): number {
  return s === "severe" ? 3 : s === "medium" ? 2 : 1;
}

function exampleForRow(row: UtmRow): string | null {
  const parts = [row.source, row.medium, row.campaign].filter(Boolean);
  return parts.length ? parts.join(" · ") : (row.url ?? null);
}

/** Build a CSV of flagged rows for the download-bad-rows feature. */
export function flaggedToCsv(flagged: UtmScoreResult["flagged"]): string {
  return toCsv(
    [
      "url",
      "utm_source",
      "utm_medium",
      "utm_campaign",
      "utm_content",
      "utm_term",
      "utm_source_platform",
      "sessions",
      "issues",
    ],
    flagged.map((r) => [
      r.url ?? "",
      r.source ?? "",
      r.medium ?? "",
      r.campaign ?? "",
      r.content ?? "",
      r.term ?? "",
      r.sourcePlatform ?? "",
      String(r.sessions ?? ""),
      r.issues.join("|"),
    ]),
  );
}
